import { openDB } from 'idb'
import { CHAT_KIND } from './dm.js'

const DB_NAME = 'saysheep'
const DB_VERSION = 2

let _db = null

const db = async () => {
  if (_db) return _db
  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const events = db.createObjectStore('events', { keyPath: 'id' })
        events.createIndex('kind', 'kind')
        events.createIndex('created_at', 'created_at')
        events.createIndex('pubkey', 'pubkey')
        db.createObjectStore('meta', { keyPath: 'key' })
      }
      if (oldVersion < 2) {
        db.createObjectStore('gazetteer', { keyPath: 'prefix' })
      }
    },
  })
  return _db
}

export const getGazetteerTileLocal = async (prefix) => {
  const d = await db()
  const tx = d.transaction('gazetteer', 'readwrite')
  const tile = await tx.store.get(prefix)
  if (tile) {
    tile.timestamp = Date.now()
    await tx.store.put(tile)
    await tx.done
    return tile
  }
  await tx.done
  return null
}

export const saveGazetteerTileLocal = async (prefix, places, version) => {
  const d = await db()
  const tx = d.transaction('gazetteer', 'readwrite')
  await tx.store.put({
    prefix,
    places,
    version,
    timestamp: Date.now()
  })
  const all = await tx.store.getAll()
  if (all.length > 50) {
    all.sort((a, b) => a.timestamp - b.timestamp)
    const toDelete = all.length - 50
    for (let i = 0; i < toDelete; i++) {
      await tx.store.delete(all[i].prefix)
    }
  }
  await tx.done
}

export const storeEvent = async (event) => {
  const cleanEvent = JSON.parse(JSON.stringify(event))
  const d = await db()
  const tx = d.transaction('events', 'readwrite')

  // NIP-09 kind 5: deletion event
  if (cleanEvent.kind === 5) {
    const eTags = cleanEvent.tags.filter(t => t[0] === 'e').map(t => t[1])
    for (const id of eTags) {
      const target = await tx.store.get(id)
      if (target && target.pubkey === cleanEvent.pubkey) {
        await tx.store.delete(id)
      }
    }
  }

  // NIP-33: for kind 30402, replace older events with same (pubkey, d-tag)
  if (cleanEvent.kind === 30402) {
    const dTag = cleanEvent.tags.find(t => t[0] === 'd')?.[1]
    if (dTag) {
      const all = await tx.store.index('kind').getAll(30402)
      for (const ev of all) {
        if (ev.pubkey === cleanEvent.pubkey &&
            ev.tags.find(t => t[0] === 'd')?.[1] === dTag &&
            ev.id !== cleanEvent.id) {
          if (ev.created_at >= cleanEvent.created_at) {
            await tx.done; return // already have newer
          }
          await tx.store.delete(ev.id) // remove stale
        }
      }
    }
  }

  const existing = await tx.store.get(cleanEvent.id)
  if (!existing) await tx.store.put(cleanEvent)
  await tx.done
}

export const getEvent = async (id) => {
  return (await db()).get('events', id)
}

export const getEventsByKind = async (kind, limit = 500) => {
  const d = await db()
  const index = d.transaction('events').store.index('kind')
  return index.getAll(kind, limit)
}

// Get items by geohash prefix — filter client-side
export const getItemsByGeohash = async (geohashPrefix) => {
  const all = await getEventsByKind(30402)
  const claimants = await getEventsByKind(30403)
  return [...all, ...claimants].filter(ev => {
    if (ev.kind === 30403) return true
    const gTags = ev.tags.filter(t => t[0] === 'g').map(t => t[1])
    return gTags.some(g => g.startsWith(geohashPrefix) || geohashPrefix.startsWith(g))
  })
}

// All cached private-chat (NIP-44) DM events, replayed on startup so threads
// survive reloads and show up offline.
export const getDMs = async () => getEventsByKind(CHAT_KIND)

export const deleteEvent = async (id) => {
  (await db()).delete('events', id)
}

export const purgeExpired = async () => {
  const now = Math.floor(Date.now() / 1000)
  const maxAge = now - 14 * 86400
  const d = await db()
  const all = await d.getAll('events')
  const tx = d.transaction('events', 'readwrite')
  for (const ev of all) {
    const expiryTag = ev.tags?.find(t => t[0] === 'expiry')
    const expiry = expiryTag ? parseInt(expiryTag[1]) : null
    if ((expiry && expiry < now) || ev.created_at < maxAge) {
      tx.store.delete(ev.id)
    }
  }
  await tx.done
}

export const getMeta = async (key) => {
  const row = await (await db()).get('meta', key)
  return row?.value
}

export const setMeta = async (key, value) => {
  (await db()).put('meta', { key, value })
}
